/**
 * ActivitiesSettingsSection — Activities sub-screen body.
 * Web port of client-native ActivitiesSettingsScreen + FeedActivitiesSettingsScreen.
 *
 * iOS splits these into two routes, but its Settings hub links to only ONE
 * "Activities" row (Feed Activities is not a hub item), so web keeps a single
 * "Activities" sub-screen with the feed-activities controls as a sub-section —
 * parity-correct at the hub level. The 6 lesson-activity toggles (matching iOS),
 * then a "Feed Activities" sub-section (enable toggle + Number of Activities
 * stepper). Copy matches iOS verbatim.
 */
import { FC, useMemo } from "react";
import { colors } from "@/ui";
import { SettingsToggle } from "../SettingsToggle";
import HelpTooltip from "./HelpTooltip";
import Stepper from "./Stepper";
import { useSettingsToggle } from "./useSettingsToggle";

const ACTIVITY_COUNT_OPTIONS = [
  { count: 2, displayName: "Fewer", description: "2 activities" },
  { count: 3, displayName: "Baseline", description: "3 activities" },
  { count: 4, displayName: "More", description: "4 activities" },
  { count: 5, displayName: "Most", description: "5 activities" },
];

const ActivitiesSettingsSection: FC = () => {
  const { settings, isLoading, toggle, updateSettings } = useSettingsToggle();

  const activityCount = settings?.activityCount ?? 3;
  const activityCountIndex = useMemo(() => {
    const i = ACTIVITY_COUNT_OPTIONS.findIndex((o) => o.count === activityCount);
    return i < 0 ? 1 : i;
  }, [activityCount]);
  const activityCountOption = ACTIVITY_COUNT_OPTIONS[activityCountIndex];

  const setCount = (i: number) => {
    void updateSettings({ activityCount: ACTIVITY_COUNT_OPTIONS[i].count }).catch(
      () => {}
    );
  };

  return (
    <div>
      <p
        className="text-sm"
        style={{ color: colors.secondary, marginBottom: 12 }}
      >
        Enable or disable the activities that show up in video lessons
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <SettingsToggle
          label="Word List"
          isChecked={settings?.isWordListEnabled || false}
          onChange={() => toggle("isWordListEnabled")}
          isDisabled={isLoading}
          helpContent={
            <div>
              <p><strong>Word List Activity</strong></p>
              <p>A list of words that were not pressed during the video lessons. This screen enables you to move all these words to known, or individual words in a more convenient way.</p>
            </div>
          }
        />
        <SettingsToggle
          label="Guess the Word"
          isChecked={settings?.isGuessTheWordEnabled || false}
          onChange={() => toggle("isGuessTheWordEnabled")}
          isDisabled={isLoading}
          helpContent={
            <div>
              <p><strong>Guess the Word Activity</strong></p>
              <p>Test your vocabulary by guessing the meaning of words or phrases from the video lessons.</p>
            </div>
          }
        />
        <SettingsToggle
          label="Fill in the Blank"
          isChecked={settings?.isFillInTheBlankEnabled || false}
          onChange={() => toggle("isFillInTheBlankEnabled")}
          isDisabled={isLoading}
          helpContent={
            <div>
              <p><strong>Fill in the Blank Activity</strong></p>
              <p>Complete sentences by listening to the video and filling in the missing word from a multiple choice list.</p>
            </div>
          }
        />
        <SettingsToggle
          label="Match the Words"
          isChecked={settings?.isMatchTheWordsEnabled || false}
          onChange={() => toggle("isMatchTheWordsEnabled")}
          isDisabled={isLoading}
          helpContent={
            <div>
              <p><strong>Match the Words Activity</strong></p>
              <p>Connect words with their translations or meanings.</p>
            </div>
          }
        />
        <SettingsToggle
          label="Spell the Word"
          isChecked={settings?.isSpellTheWordEnabled || false}
          onChange={() => toggle("isSpellTheWordEnabled")}
          isDisabled={isLoading}
          helpContent={
            <div>
              <p><strong>Spell the Word Activity</strong></p>
              <p>Practice spelling and translating words from the video lessons by typing them out by each letter or by syllables.</p>
            </div>
          }
        />
        <SettingsToggle
          label="Build the Sentence"
          isChecked={settings?.isBuildTheSentenceEnabled || false}
          onChange={() => toggle("isBuildTheSentenceEnabled")}
          isDisabled={isLoading}
          helpContent={
            <div>
              <p><strong>Build the Sentence Activity</strong></p>
              <p>Practice listening by arranging words in the correct order to build complete sentences after hearing them pronounced.</p>
            </div>
          }
        />
      </div>

      {/* Feed Activities (iOS FeedActivitiesSettingsScreen) */}
      <div style={{ height: 1, backgroundColor: colors.greyLightest, margin: "16px 0" }} />
      <div className="text-base font-semibold" style={{ color: colors.primary, marginBottom: 12 }}>
        Feed Activities
      </div>
      <SettingsToggle
        label="Enable Feed Activities"
        isChecked={settings?.areFeedActivitiesEnabled || false}
        onChange={() => toggle("areFeedActivitiesEnabled")}
        isDisabled={isLoading}
        helpContent={
          <div>
            <p><strong>Feed Activities</strong></p>
            <p>Enable or disable practice activities that appear between video lessons in the feed.</p>
          </div>
        }
      />
      <div style={{ marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span className="text-base font-bold" style={{ color: colors.primary }}>
            Number of Activities
          </span>
          <HelpTooltip
            title="Number of Activities"
            content="Control how many activities appear after each video lesson. The actual number may vary based on how many words you're learning in that lesson. Activities help reinforce what you've learned."
          />
        </div>
        <p className="text-sm" style={{ color: colors.secondary, marginTop: 4, marginBottom: 8 }}>
          Target number of activities after each lesson (applies to both lesson path and feed). Actual count depends on available learning words.
        </p>
        <Stepper
          canDecrease={activityCountIndex > 0}
          canIncrease={activityCountIndex < ACTIVITY_COUNT_OPTIONS.length - 1}
          onDecrease={() => setCount(activityCountIndex - 1)}
          onIncrease={() => setCount(activityCountIndex + 1)}
        >
          <div
            style={{
              padding: 8,
              borderRadius: 12,
              backgroundColor: colors.white,
              border: `1px solid ${colors.greyLight}`,
              textAlign: "center",
            }}
          >
            <div className="text-base font-bold" style={{ color: colors.primary }}>
              {activityCountOption.displayName}
            </div>
            <div className="text-sm" style={{ color: colors.secondary }}>
              {activityCountOption.description}
            </div>
          </div>
        </Stepper>
      </div>
    </div>
  );
};

export default ActivitiesSettingsSection;
